import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider } from "@/lib/auth";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { Toaster } from "@/components/ui/sonner";

import Landing from "@/pages/Landing";
import ExternalLogin from "@/pages/ExternalLogin";
import SsoCallback from "@/pages/SsoCallback";
import AuthCallback from "@/pages/AuthCallback";
import AppLayout from "@/components/AppLayout";
import Dashboard from "@/pages/Dashboard";
import HifzPlan from "@/pages/HifzPlan";
import BasicHifz from "@/pages/BasicHifz";
import SurahView from "@/pages/SurahView";
import Tadabur from "@/pages/Tadabur";
import TafsirReader from "@/pages/TafsirReader";
import Memorize from "@/pages/Memorize";
import Revision from "@/pages/Revision";
import Achievements from "@/pages/Achievements";
import Progress from "@/pages/Progress";
import Halaqa from "@/pages/Halaqa";
import Leaderboard from "@/pages/Leaderboard";
import TeacherDashboard from "@/pages/TeacherDashboard";
import Pulse from "@/pages/Pulse";
import PulseShare from "@/pages/PulseShare";
import MySubmissions from "@/pages/MySubmissions";
import GradingInbox from "@/pages/GradingInbox";
import HalaqaPulse from "@/pages/HalaqaPulse";
import AdminPanel from "@/pages/AdminPanel";
import School from "@/pages/School";
import OrgAdmin from "@/pages/OrgAdmin";
import VoiceNotes from "@/pages/VoiceNotes";
import Encourage from "@/pages/Encourage";
import HifzTest from "@/pages/HifzTest";
import ContinueJourney from "@/pages/ContinueJourney";
import MyQuran from "@/pages/MyQuran";
import Community from "@/pages/Community";
import Profile from "@/pages/Profile";
import Legacy from "@/pages/Legacy";
import TeacherApply from "@/pages/TeacherApply";
import TeacherApplications from "@/pages/TeacherApplications";
import Teachers from "@/pages/Teachers";
import TeacherProfile from "@/pages/TeacherProfile";
import Bookings from "@/pages/Bookings";
import GatewayDemoSwitcher from "@/components/demo/GatewayDemoSwitcher";
import { tokenStore } from "@/lib/api";

function AppRouter() {
  const location = useLocation();
  // Capture referral code from any shared link (?ref=xxxx)
  React.useEffect(() => {
    const ref = new URLSearchParams(location.search).get("ref");
    if (ref) localStorage.setItem("hwh_ref", ref);
  }, [location.search]);
  // Capture institution invite code from any shared link (?org=xxxx)
  React.useEffect(() => {
    const org = new URLSearchParams(location.search).get("org");
    if (org) localStorage.setItem("hwh_org", org);
  }, [location.search]);
  // Synchronously detect Google OAuth callback session_id in URL fragment
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<ExternalLogin mode={new URLSearchParams(location.search).get("mode")} />} />
      <Route path="/sso/callback" element={<SsoCallback />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/pulse/:token" element={<PulseShare />} />
      <Route path="/encourage/:token" element={<Encourage />} />
      <Route path="/app" element={<AppLayout />}>
        <Route index element={<Dashboard />} />
        <Route path="continue" element={<ContinueJourney />} />
        {/* Today's Plan is merged into Home — keep old links working */}
        <Route path="plan" element={<Navigate to="/app" replace />} />
        <Route path="quran" element={<MyQuran />} />
        <Route path="community" element={<Community />} />
        <Route path="legacy" element={<Legacy />} />
        <Route path="teacher-apply" element={<TeacherApply />} />
        <Route path="teachers" element={<Teachers />} />
        <Route path="teachers/:teacherId" element={<TeacherProfile />} />
        <Route path="bookings" element={<Bookings />} />
        <Route path="admin/teacher-apps" element={<TeacherApplications />} />
        <Route path="profile" element={<Profile />} />
        <Route path="hifz" element={<HifzPlan />} />
        <Route path="basic-hifz" element={<BasicHifz />} />
        <Route path="hifz/:surah" element={<SurahView />} />
        {/* Mushaf/Qari/Search merged into the My Quran reader (tabs) */}
        <Route path="mushaf" element={<Navigate to="/app/quran" replace />} />
        {/* Legacy /recitation route redirects to unified Hifz Test */}
        <Route path="recitation" element={<Navigate to="/app/hifz-test?mode=whisper" replace />} />
        <Route path="hifz-test" element={<HifzTest />} />
        <Route path="tadabur" element={<Navigate to="/app/tadabbur" replace />} />
        <Route path="tadabbur" element={<Tadabur />} />
        <Route path="tadabbur/:surah" element={<TafsirReader />} />
        <Route path="memorize/:surah/:ayah" element={<Memorize />} />
        <Route path="revision" element={<Revision />} />
        <Route path="achievements" element={<Achievements />} />
        <Route path="progress" element={<Progress />} />
        <Route path="qari" element={<Navigate to="/app/quran?tab=listen" replace />} />
        <Route path="halaqa" element={<Halaqa />} />
        <Route path="leaderboard" element={<Leaderboard />} />
        <Route path="pulse" element={<Pulse />} />
        <Route path="submissions" element={<MySubmissions />} />
        <Route path="teacher" element={<TeacherDashboard />} />
        <Route path="teacher/grading" element={<GradingInbox />} />
        <Route path="grading" element={<GradingInbox />} />
        <Route path="teacher/halaqa-pulse" element={<HalaqaPulse />} />
        <Route path="voice-notes" element={<VoiceNotes />} />
        <Route path="search" element={<Navigate to="/app/quran?tab=search" replace />} />
        <Route path="admin" element={<AdminPanel />} />
        <Route path="school" element={<School />} />
        <Route path="org-admin" element={<OrgAdmin />} />
        {/* a mistyped or retired /app/... address lands on the home page, not a blank screen */}
        <Route path="*" element={<Navigate to="/app" replace />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <LanguageProvider>
          <AuthProvider>
            <AppRouter />
            <GatewayDemoSwitcher gatewayUrl={process.env.REACT_APP_GATEWAY_URL} getToken={tokenStore.get} setToken={tokenStore.set}
              homes={{ default: "/app", principal: "/app/school", teacher: "/app/school" }} />
            <Toaster position="top-center" richColors />
          </AuthProvider>
        </LanguageProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
